import * as React from "react"
import {
  Text,
  HStack,
  Image,
  useColorMode,
} from "@chakra-ui/react"
import { brandConfig, getColor } from "./brandConfig"
import { useBrandVersion } from "./components/TenantBrandingLoader"

/**
 * Brand logo (navbar + footer). Brand-driven since 2026-09-02:
 *
 *  - `brandConfig.logo.imageUrl` set (overlaid from Tenant.branding.logo, or
 *    written into brandConfig.ts by scripts/apply-brand-spec.js) → the image
 *    lockup, height-bounded, never cropped (wordmarks are wide; a square
 *    `object-fit: cover` would cut 80% of a 5:1 logo away).
 *  - otherwise → glyph + siteName as text in brand colours.
 *
 * `brandConfig.hideSiteName` suppresses the text beside an image logo (a
 * wordmark already carries the name). Pass `height` to size the image.
 */
export const Logo = React.forwardRef<HTMLDivElement, any>(({ height, ...props }, ref) => {
  const { colorMode } = useColorMode();
  useBrandVersion(); // re-render once the tenant overlay lands (brandConfig is mutated in place)
  // Colour-mode lockup: dark mode → logoOnDark, light mode → logoOnLight, each
  // falling back to the default `logo`. A light-background wordmark on a dark
  // navbar is invisible (TMS, 2026-09-02), hence the two slots.
  const imageUrl =
    (colorMode === "dark" ? brandConfig.logo.imageUrlDark : brandConfig.logo.imageUrlLight) || brandConfig.logo.imageUrl;
  // A wide image (aspect > 2:1) is a wordmark — it already carries the name, so
  // the text is never repeated beside it regardless of hideSiteName. Only a
  // square-ish mark gets the name printed next to it (and hideSiteName can
  // still suppress that). Aspect is unknown until the image loads: default to
  // "wide" so a wordmark never flashes a duplicate name. (Tom, 2026-09-02:
  // "we see the logo twice or the name twice".)
  const [isWordmark, setIsWordmark] = React.useState(true);
  // If the image URL the record points at cannot load (deleted asset, dead
  // host), fall back to the text lockup instead of an empty navbar slot.
  const [failedSrc, setFailedSrc] = React.useState<string | null>(null);
  const showName = !brandConfig.hideSiteName && !isWordmark;

  if (imageUrl && failedSrc !== imageUrl) {
    return (
      <HStack ref={ref} spacing={3} align="center" {...props}>
        <Image
          src={imageUrl}
          alt={brandConfig.logo.alt || brandConfig.siteName}
          h={height ?? { base: "28px", md: "34px" }}
          maxW="240px"
          objectFit="contain"
          objectPosition="left center"
          loading="eager"
          draggable={false}
          onLoad={(e) => {
            const img = e.currentTarget;
            if (img.naturalWidth && img.naturalHeight) setIsWordmark(img.naturalWidth / img.naturalHeight > 2);
          }}
          onError={() => setFailedSrc(imageUrl)}
        />
        {showName && (
          <Text
            fontSize={{ base: "md", md: "lg" }}
            fontWeight="700"
            color={getColor("text.primary", colorMode)}
            letterSpacing="-0.02em"
            whiteSpace="nowrap"
          >
            {brandConfig.siteName}
          </Text>
        )}
      </HStack>
    );
  }

  return (
    <HStack ref={ref} spacing={2} align="center" {...props}>
      {brandConfig.logo.glyph && (
        <Text fontSize={{ base: "lg", md: "xl" }} lineHeight="1" aria-hidden>
          {brandConfig.logo.glyph}
        </Text>
      )}
      <Text
        fontSize={{ base: "lg", md: "xl" }}
        fontWeight="800"
        color={getColor("primary", colorMode)}
        letterSpacing="-0.03em"
        whiteSpace="nowrap"
      >
        {brandConfig.siteName}
      </Text>
    </HStack>
  )
})

Logo.displayName = 'Logo'

export default Logo;
